import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { ExchangeService } from '@vaki/shared/data-access/exchange';
import {
  CurrencyFormatPipe,
  IsVakiClosedPipe
} from '@vaki/shared/util/front-end';
import {
  GA4ClickEvents,
  MIN_CONTRIBUTION_ARS,
  MIN_CONTRIBUTION_COP,
  Vaki,
  VakiAnalytics
} from '@vaki/shared/util/interfaces';
import { Observable, of, Subscription } from 'rxjs';

import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { AuthService } from '@vaki/shared/auth/data-access';
import { AlgoliaService } from '@vaki/shared/data-access/algolia';
import { MatInputNumberDirective } from '@vaki/shared/util/front-end';
import { LogAnalyticsDirective } from '@vaki/shared/util/ga4';
import { ExploreStateService } from '@vaki/vakers/vakis/data-access';

@Component({
  selector: 'lib-vakers-vakis-ui-support-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatInputModule,
    MatInputNumberDirective,
    NgIf,
    MatButtonModule,
    RouterLink,
    LogAnalyticsDirective,
    DecimalPipe,
    CurrencyFormatPipe,
    IsVakiClosedPipe
  ],
  templateUrl: './support-form.component.html',
  styleUrl: './support-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VakersVakisUiSupportFormComponent implements OnChanges, OnDestroy {
  @Input() vaki: Vaki | undefined;
  @Input() vakiAnalytics: VakiAnalytics | undefined | null;
  @Input() available: boolean | undefined;
  @Input() currentCurrency: string | null | undefined = 'COP';
  @Input() stickyMode = false;
  @Input() currencies: string[] | undefined | null;
  @Output() currencyChange = new EventEmitter();

  contribution: UntypedFormGroup;
  subscription: Subscription | undefined;
  public readonly ga4ClickEvents = GA4ClickEvents;

  readonly algoliaService = inject(AlgoliaService);
  readonly authService = inject(AuthService);
  readonly exploreStateService = inject(ExploreStateService);

  constructor(
    protected readonly formBuilder: UntypedFormBuilder,
    protected readonly exchangeService: ExchangeService
  ) {
    this.contribution = this.formBuilder.group({
      currency: new FormControl<string>(this.currentCurrency ?? 'COP'),
      value: new FormControl<number>(0, Validators.required)
    });
    this.subscription = this.contribution
      ?.get('currency')
      ?.valueChanges.subscribe((value) => this.currencyChange.emit(value));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  ngOnChanges({ currentCurrency }: SimpleChanges): void {
    if (currentCurrency) {
      this.contribution.patchValue({ currency: this.currentCurrency });
      const currency = currentCurrency.currentValue === 'ARS' ? 'ARS' : 'COP';
      const minContribution =
        currentCurrency.currentValue === 'ARS'
          ? MIN_CONTRIBUTION_ARS
          : MIN_CONTRIBUTION_COP;

      const minValue = Math.ceil(
        this.exchangeService.convert(
          currency,
          currentCurrency.currentValue,
          minContribution
        )
      );
      const valueControl = this.contribution.get('value');
      valueControl?.setValidators([Validators.min(minValue)]);
      valueControl?.updateValueAndValidity();
    }
  }

  createMinValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const { value, currency } = control.value;
      const error = { min_contribution_error: true };
      if (value <= 0) {
        return of(error);
      } else {
        return this.exchangeService.convert(currency, 'COP', value) >=
          MIN_CONTRIBUTION_COP
          ? of(null)
          : of(error);
      }
    };
  }
}
